import type { FieldSpecOf } from 'v5/core/entity-schema/entitySchema.types';
import type {
  DimensionValue,
  FieldCodec,
  LegacyMeasurementValue
} from 'v5/core/entity-values/values.types';
import { customFieldColumns } from './customFieldColumns';
import { findCustomFieldRowForRead } from './customFieldRows';
import { validateField, validation } from './validation';

type DimensionSpec = FieldSpecOf<'dimension'>;

/** First unit of the definition's list, as legacy `MeasurementInput` defaulted. */
export function dimensionDefaultUnit(spec: DimensionSpec): string {
  return spec.units[0]?.value ?? '';
}

function emptyAxis(spec: DimensionSpec): LegacyMeasurementValue {
  return { amount: null, unit: dimensionDefaultUnit(spec) };
}

/** Legacy rows hold amounts as numbers or strings (`TextInputGroup` output); anything non-numeric reads as `null`. */
function parseAmount(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw === 'string') {
    if (raw.trim() === '') return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function parseAxis(raw: unknown, spec: DimensionSpec): LegacyMeasurementValue {
  if (typeof raw !== 'object' || raw === null) return emptyAxis(spec);
  const record = raw as Record<string, unknown>;
  const unit = record.unit;
  return {
    amount: parseAmount(record.amount),
    unit:
      typeof unit === 'string' && unit !== ''
        ? unit
        : dimensionDefaultUnit(spec)
  };
}

function parseStored(json: string | null): Record<string, unknown> {
  if (json === null || json.trim() === '') return {};
  try {
    const parsed: unknown = JSON.parse(json);
    return typeof parsed === 'object' &&
      parsed !== null &&
      !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

/** `string_value` JSON `{ x, y[, z] }` → form value; `z` only for 3 axes. */
export function parseDimension(
  json: string | null,
  spec: DimensionSpec
): DimensionValue {
  const stored = parseStored(json);
  return {
    x: parseAxis(stored.x, spec),
    y: parseAxis(stored.y, spec),
    z: spec.axes === 3 ? parseAxis(stored.z, spec) : null
  };
}

/** Form value → the JSON `Dimension2DInput` / `Dimension3DInput` store. */
export function serializeDimension(
  value: DimensionValue,
  spec: DimensionSpec
): string {
  const axis = (axisValue: LegacyMeasurementValue) => ({
    amount: axisValue.amount,
    unit: axisValue.unit
  });
  return JSON.stringify(
    spec.axes === 3
      ? {
          x: axis(value.x),
          y: axis(value.y),
          z: axis(value.z ?? emptyAxis(spec))
        }
      : { x: axis(value.x), y: axis(value.y) }
  );
}

function axes(value: DimensionValue): LegacyMeasurementValue[] {
  return value.z === null ? [value.x, value.y] : [value.x, value.y, value.z];
}

function isEmpty(value: DimensionValue): boolean {
  return axes(value).every(axis => axis.amount === null);
}

/** DIMENSION_2D / DIMENSION_3D custom field: `string_value` JSON of `{ amount, unit }` per axis. */
export const dimensionCodec: FieldCodec<'dimension'> = {
  decode(entity, spec, ctx) {
    const row = findCustomFieldRowForRead(entity, spec.definition.id, ctx);
    return parseDimension(row?.string_value ?? null, spec);
  },
  encode(value, spec) {
    return {
      type: 'custom',
      columns: customFieldColumns({
        string_value: isEmpty(value) ? null : serializeDimension(value, spec)
      })
    };
  },
  empty(spec) {
    return {
      x: emptyAxis(spec),
      y: emptyAxis(spec),
      z: spec.axes === 3 ? emptyAxis(spec) : null
    };
  },
  isEmpty,
  validate(value, spec) {
    return validateField(value, spec, isEmpty, value =>
      axes(value).some(
        axis => axis.amount !== null && !Number.isFinite(axis.amount)
      )
        ? validation.invalid({ code: 'not-a-number' })
        : validation.valid()
    );
  }
};
